import { useCallback, useEffect, useRef, useState } from 'react';
import config from '../config.js';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { isSignedIn } from '../_helpers/safeRedirect.js';

/** How long the cooldown hint stays on a tapped card, in ms. */
export const VOTE_HINT_MS = 1600;

const readyOf = (fxRef) => (fxRef.current?.status === 'ready' ? fxRef.current : null);
const cardOf = (fxRef, id) => readyOf(fxRef)?.getCard(id) ?? null;

/** The upvote endpoint for one queue entry (ENG-805). */
export const upvoteUrl = (bandId, entryId) =>
  `${config.apiBaseUrl}/bands/${bandId}/queue/entries/${entryId}/upvote`;

/** The engine's setBuild input for one fx_energy song (ENG-805 frame shape). */
export const buildFor = (song) => ({
  level: Number(song.level) || 0,
  visible: song.lit ? 1 : 0,
  lit: Boolean(song.lit),
  rel: song.rel == null ? 1 : Number(song.rel) || 0,
});

/**
 * Upvotes and crowd energy on the stream page's queue (ENG-806, FX-2).
 *
 * There is no vote button: tapping a song's album art is the vote (`tap`).
 * - Voting needs a session (Tim's decision). Signed out, a tap does nothing
 *   at all: no request, no effect, no notice, no redirect. The page also
 *   offers signed-out viewers no tap target (`canVote`).
 * - The locked up-next song does nothing; #1 plays `card.notice()` ("Top
 *   spot") for the tapper only, with no request and no cooldown.
 * - Inside the cooldown (10 s per listener per band, from the server's
 *   `cooldownMs` or a 429's `retry_after_ms`) a tap sends nothing and shows a
 *   short hint on that card (`hint`). Only an accepted vote or a 429 starts it.
 * - The +1 is not played from the HTTP answer: the `fx_vote` frame
 *   (`onVoteFrame`) plays every vote, with the cherry on the voter's own
 *   sockets only (`mine`), so a vote is never shown twice.
 *
 * `onEnergyFrame` applies `fx_energy` (2 Hz while any song has backing): each
 * listed song gets `card.setBuild`, every other card `card.clearBuild()`. The
 * last frame is re-applied when the stage restarts or the queue changes, so
 * new cards pick it up. Signed out or not, every viewer sees the builds and
 * other people's votes.
 */
export const useFxVotes = ({ bandId, fx, entries = [] }) => {
  const fetchWrapperRef = useRef(useFetchWrapper());
  const fxRef = useRef(fx);
  fxRef.current = fx;
  const entryIdsRef = useRef([]);
  entryIdsRef.current = entries.map((e) => e.entryId);
  const nextVoteAtRef = useRef(0);
  const pendingRef = useRef(false);
  const lastEnergyRef = useRef(null);
  const builtIdsRef = useRef(new Set());
  const hintTimerRef = useRef(null);
  const [hint, setHint] = useState(null);

  const applyEnergy = useCallback((frame) => {
    const stage = readyOf(fxRef);
    if (!stage || !frame) return;
    const songs = new Map((Array.isArray(frame.songs) ? frame.songs : []).map((s) => [s.entryId, s]));
    const ids = new Set([...entryIdsRef.current, ...builtIdsRef.current, ...songs.keys()]);
    const built = new Set();
    ids.forEach((id) => {
      const card = stage.getCard(id);
      if (!card) return;
      const song = songs.get(id);
      if (song) {
        card.setBuild(buildFor(song));
        built.add(id);
      } else {
        card.clearBuild();
      }
    });
    builtIdsRef.current = built;
  }, []);

  const onEnergyFrame = useCallback((frame) => {
    lastEnergyRef.current = frame;
    applyEnergy(frame);
  }, [applyEnergy]);

  const onVoteFrame = useCallback((frame) => {
    cardOf(fxRef, frame?.entryId)?.vote({ mine: Boolean(frame?.mine) });
  }, []);

  // A new stage has fresh cards; a queue change may have registered new ones.
  const stage = fx?.status === 'ready' ? fx.stage : null;
  const queueKey = entryIdsRef.current.join(',');
  useEffect(() => {
    if (stage) applyEnergy(lastEnergyRef.current);
  }, [stage, queueKey, applyEnergy]);

  // The +1 is drawn in Outfit; ask for it so the canvas has it (else the system font).
  useEffect(() => {
    if (!stage) return;
    try {
      document.fonts?.load?.('700 16px Outfit')?.catch?.(() => {});
    } catch (_e) {
      // No font loading API.
    }
  }, [stage]);

  useEffect(() => () => clearTimeout(hintTimerRef.current), []);

  const showCooldown = useCallback((entryId, ms) => {
    clearTimeout(hintTimerRef.current);
    setHint({ entryId, seconds: Math.max(1, Math.ceil(ms / 1000)), at: Date.now() });
    hintTimerRef.current = setTimeout(() => setHint(null), VOTE_HINT_MS);
  }, []);

  const tap = useCallback(async (entry) => {
    // Signed out: nothing at all (no request, effect, notice or redirect).
    if (!entry || entry.locked || !isSignedIn()) return;
    if (entry.position === 1) {
      cardOf(fxRef, entry.entryId)?.notice();
      return;
    }
    const left = nextVoteAtRef.current - Date.now();
    if (left > 0) {
      showCooldown(entry.entryId, left);
      return;
    }
    if (pendingRef.current) return;
    pendingRef.current = true;
    try {
      const res = await fetchWrapperRef.current.post(upvoteUrl(bandId, entry.entryId));
      const ms = Number(res?.cooldownMs);
      nextVoteAtRef.current = Date.now() + (Number.isFinite(ms) && ms > 0 ? ms : 0);
    } catch (err) {
      if (err?.code === 'upvote_cooldown') {
        const ms = Number(err.params?.retry_after_ms);
        const wait = Number.isFinite(ms) && ms > 0 ? ms : 1000;
        nextVoteAtRef.current = Date.now() + wait;
        showCooldown(entry.entryId, wait);
      } else if (err?.code === 'upvote_not_votable' && err.params?.reason === 'top_spot') {
        // The queue moved under the tap: it is #1 now.
        cardOf(fxRef, entry.entryId)?.notice();
      }
      // next_up, playing, not waiting, network: the queue refresh will show why.
    } finally {
      pendingRef.current = false;
    }
  }, [bandId, showCooldown]);

  return { tap, hint, onVoteFrame, onEnergyFrame };
};

export default useFxVotes;
